import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./css/Ai.css";

const PYTHON_AI_API = process.env.REACT_APP_AI_ENGINE_URL;

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

function encodeWavStereo(left, right, sampleRate) {
  const length = Math.min(left.length, right.length);
  const bytesPerSample = 2;
  const numberOfChannels = 2;
  const blockAlign = numberOfChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = length * blockAlign;

  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");

  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);

  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;

  for (let i = 0; i < length; i++) {
    const l = Math.max(-1, Math.min(1, left[i]));
    const r = Math.max(-1, Math.min(1, right[i]));

    view.setInt16(offset, l < 0 ? l * 0x8000 : l * 0x7fff, true);
    offset += 2;

    view.setInt16(offset, r < 0 ? r * 0x8000 : r * 0x7fff, true);
    offset += 2;
  }

  return buffer;
}

function stemToUrl(stem, sampleRate) {
  const wavBuffer = encodeWavStereo(stem.left, stem.right, sampleRate);
  const blob = new Blob([wavBuffer], { type: "audio/wav" });
  return URL.createObjectURL(blob);
}

function AiMusic() {
  const navigate = useNavigate();

  const [mp3File, setMp3File] = useState(null);
  const [loading, setLoading] = useState(false);

  const [tempo, setTempo] = useState(null);
  const [duration, setDuration] = useState(null);
  const [stems, setStems] = useState(null);

  const [log, setLog] = useState("");
  const [backendTest, setBackendTest] = useState(null);

  const [downloadPercent, setDownloadPercent] = useState(0);
  const [separatePercent, setSeparatePercent] = useState(0);

  const audioRefs = {
    drums: useRef(null),
    bass: useRef(null),
    vocals: useRef(null),
    instrument: useRef(null),
  };

  const clearStemUrls = () => {
    if (!stems) return;

    Object.values(stems).forEach((url) => {
      if (url && url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    });
  };

  const resetResults = () => {
    clearStemUrls();
    setTempo(null);
    setDuration(null);
    setStems(null);
    setBackendTest(null);
    setDownloadPercent(0);
    setSeparatePercent(0);
    setLog("");
  };

  const stopAll = () => {
    Object.values(audioRefs).forEach((ref) => {
      if (ref.current) {
        ref.current.pause();
        ref.current.currentTime = 0;
      }
    });
  };

  const testBackendDate = async () => {
    setLoading(true);
    setLog("Testing Python backend date route...");
    setBackendTest(null);

    try {
      const res = await fetch(`${PYTHON_AI_API}/ai/music/test-date`, {
        method: "GET",
      });

      const text = await res.text();

      let data;

      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(`Backend did not return JSON. Raw: ${text}`);
      }

      if (!res.ok || !data.success) {
        throw new Error(data?.detail || "Backend test failed");
      }

      setBackendTest(data);
      setLog("Backend date test successful.");
    } catch (err) {
      setLog(`Backend test error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const separateInBrowser = async () => {
    if (!mp3File) {
      setLog("Please upload an audio file first.");
      return;
    }

    resetResults();
    setLoading(true);
    setLog("Starting browser Demucs engine...");

    try {
      const [{ DemucsProcessor, CONSTANTS }, ort] = await Promise.all([
        import("demucs-web"),
        import("onnxruntime-web"),
      ]);

      if (ort.env?.wasm) {
        ort.env.wasm.numThreads = Math.min(
          navigator.hardwareConcurrency || 4,
          4,
        );
      }

      let executionProviders = ["wasm"];

      if ("gpu" in navigator) {
        try {
          const adapter = await navigator.gpu.requestAdapter();

          if (adapter) {
            executionProviders = ["webgpu", "wasm"];
            ort.env.webgpu = {
              powerPreference: "high-performance",
            };
          }
        } catch {
          executionProviders = ["wasm"];
        }
      }

      const processor = new DemucsProcessor({
        ort,
        sessionOptions: {
          executionProviders,
          enableCpuMemArena: false,
          enableMemPattern: false,
          graphOptimizationLevel: "basic",
        },
        onDownloadProgress: (loaded, total) => {
          if (!total) return;

          const percent = Math.round((loaded / total) * 100);
          setDownloadPercent(percent);
          setLog(`Downloading Demucs model: ${percent}%`);
        },
        onProgress: ({ progress, currentSegment, totalSegments }) => {
          const percent = Math.round(progress * 100);
          setSeparatePercent(percent);
          setLog(
            `Separating stems: ${percent}% (${currentSegment}/${totalSegments})`,
          );
        },
        onLog: (phase, message) => {
          console.log(`[${phase}] ${message}`);
        },
      });

      setLog("Loading Demucs model...");
      await processor.loadModel(CONSTANTS.DEFAULT_MODEL_URL);

      setLog("Decoding audio...");
      const audioContext = new AudioContext({
        sampleRate: CONSTANTS.SAMPLE_RATE,
      });

      const arrayBuffer = await mp3File.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      setDuration(Number(audioBuffer.duration.toFixed(2)));

      const leftChannel = audioBuffer.getChannelData(0);
      const rightChannel =
        audioBuffer.numberOfChannels > 1
          ? audioBuffer.getChannelData(1)
          : leftChannel;

      setLog("Running browser stem separation...");
      const result = await processor.separate(leftChannel, rightChannel);

      const generatedStems = {
        drums: stemToUrl(result.drums, CONSTANTS.SAMPLE_RATE),
        bass: stemToUrl(result.bass, CONSTANTS.SAMPLE_RATE),
        vocals: stemToUrl(result.vocals, CONSTANTS.SAMPLE_RATE),
        instrument: stemToUrl(result.other, CONSTANTS.SAMPLE_RATE),
      };

      setStems(generatedStems);
      setSeparatePercent(100);
      setLog("Browser stem separation complete.");

      await audioContext.close();
    } catch (err) {
      console.error(err);
      setLog(
        `Browser Demucs error: ${
          err?.message ||
          "Failed. Try Chrome/Edge and a short 10–20 second audio clip first."
        }`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-wrap">
      <div className="ai-bg" />

      <div className="ai-tabs">
        <button className="ai-tab" onClick={() => navigate("/ai")}>
          AI RAG
        </button>

        <button className="ai-tab active">AI Music</button>
      </div>

      <h2>AI Music</h2>

      <header className="ai-header">
        <div className="ai-brand">
          <div className="ai-badge">HX</div>
          <div>
            <div className="ai-title">HXABYTE AI MUSIC LAB</div>
            <div className="ai-subtitle">
              Browser Demucs • Stem Separation • Backend Test
            </div>
          </div>
        </div>

        <div className="ai-status">
          <span className={`dot ${loading ? "dot-live" : "dot-idle"}`} />
          <span>{loading ? "PROCESSING" : "READY"}</span>
        </div>
      </header>

      <main className="ai-grid">
        <section className="panel">
          <div className="panel-title">Upload Music</div>

          <label className={`upload ${loading ? "disabled" : ""}`}>
            <input
              type="file"
              accept=".mp3,.wav,.m4a,audio/*"
              disabled={loading}
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setMp3File(file);
                resetResults();
              }}
            />

            <span>{mp3File ? mp3File.name : "Upload MP3 / WAV"}</span>
            <span className="hint">
              Browser separates drums, bass, vocals and instrument locally
            </span>
          </label>

          <button
            className={`send ${mp3File && !loading ? "" : "disabled"}`}
            onClick={separateInBrowser}
            disabled={!mp3File || loading}
          >
            {loading ? "Processing..." : "Separate in Browser"}
          </button>

          <button
            className="send"
            onClick={testBackendDate}
            disabled={loading}
            style={{ marginTop: "10px" }}
          >
            Test Backend Date
          </button>

          {downloadPercent > 0 && downloadPercent < 100 ? (
            <div className="log">Model Download: {downloadPercent}%</div>
          ) : null}

          {separatePercent > 0 ? (
            <div className="log">Separation Progress: {separatePercent}%</div>
          ) : null}

          {tempo ? (
            <div className="log">Detected Tempo: {tempo} BPM</div>
          ) : null}

          {duration ? (
            <div className="log">Duration: {duration} seconds</div>
          ) : null}

          {log ? <div className="log">{log}</div> : null}

          {backendTest ? (
            <div className="log">
              Backend UTC Date: {backendTest.serverTimeUTC}
              <br />
              Timestamp: {backendTest.serverTimestamp}
              <br />
              Service: {backendTest.service}
            </div>
          ) : null}
        </section>

        <section className="panel chat">
          <div className="panel-title">Separated Stems</div>

          {!stems ? (
            <div className="empty">
              Upload a short audio file and click Separate in Browser.
              <br />
              First run downloads the AI model, so use Chrome/Edge and wait.
            </div>
          ) : (
            <div className="stem-list">
              {[
                ["drums", "Drums"],
                ["bass", "Bass"],
                ["vocals", "Vocals"],
                ["instrument", "Instrument / Other"],
              ].map(([stemKey, stemLabel]) => (
                <div className="stem-card" key={stemKey}>
                  <div className="stem-title">{stemLabel}</div>

                  <audio
                    ref={audioRefs[stemKey]}
                    controls
                    src={stems[stemKey]}
                  />

                  <a
                    className="stem-download"
                    href={stems[stemKey]}
                    download={`${stemKey}.wav`}
                  >
                    Download {stemLabel}
                  </a>
                </div>
              ))}

              <button className="send" onClick={stopAll}>
                Stop All
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default AiMusic;
